import { PathTool } from '../path/Path.tool'
import { convertAbsoluteDistanceToFormattedString } from '../../../../../../components/markup/utils/helpers'
import { DRAWING_TYPES } from '../../../../../../shared/constants/drawable-types'
import { applyScaleFactorToPathLength } from '../../../../../../utils/calculations/scaleConversion/scaleConversion'
import {
    AnalyticsActionEdgeType,
    AnalyticsActionType,
    Coordinates2D,
    MouseButtonCodes,
    PaperToolConfig,
    Vector2D,
} from '../../../../../types'

export class RadiusLine extends PathTool {
    static NAME = 'RADIUS LINE'

    private static readonly NUM_POINTS_IN_LINE = 3
    private static readonly COLLINEAR_TOLERANCE = 1e-6
    private lineAndCircleColor: paper.Color = new this.paper.Color('blue')
    private linePoints: Coordinates2D = []
    private temporaryPointObjects: paper.Group[] = []
    private temporaryPath: paper.Path | null = null

    constructor(config: PaperToolConfig) {
        super(config)
        this.name = RadiusLine.NAME
    }

    private cleanUpPoints = (): void => {
        this.temporaryPointObjects.forEach((path) => {
            path.remove()
        })
        this.temporaryPointObjects = []
        this.linePoints = []
    }

    /**
     * on cancel, if there is
     * any stray single points
     * clear those points
     */
    cancel = (): void => {
        this.cleanUpPoints()
        this.temporaryPath?.remove() // remove the temporary drawing path
        this.setState('common', { cursor: this.cursor, tooltip: { title: '', visible: false, color: '#000000' } })
        this.mediator.mediate('2D', {
            latestAnalyticsAction: {
                startOrStop: AnalyticsActionEdgeType.STOP,
                actionType: AnalyticsActionType.CREATE,
            },
        })
    }

    /**
     * on mouse move draw a new line that represents what the created line would look like
     * this helps the user understand where the line is
     */
    public onMouseMove = (event: paper.ToolEvent): void => {
        if (this.linePoints.length) {
            // remove the previous line that is no longer relevant
            this.temporaryPath?.remove() // remove the temporary drawing path

            const newPoint = event.point

            if (this.linePoints.length === RadiusLine.NUM_POINTS_IN_LINE - 1) {
                this.temporaryPath = new this.paper.Path([
                    this.linePoints[0],
                    this.temporaryPointObjects[1].position,
                    newPoint,
                ])
            } else {
                this.temporaryPath = new this.paper.Path([this.temporaryPointObjects[0].position, newPoint])
            }

            this.temporaryPath.strokeColor = this.lineAndCircleColor
            this.temporaryPath.strokeWidth = this.strokeWidth
            this.mediator.mediate('common', {
                tooltip: {
                    title: `${convertAbsoluteDistanceToFormattedString(
                        applyScaleFactorToPathLength({
                            pxValue: this.temporaryPath.length,
                            scaleFactor: this.scaleFactor,
                            dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                            xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                            yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                            coordinates: this.linePoints,
                            pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                        })
                    )}`,
                    visible: true,
                    color: '#000000',
                },
            })
        }
    }

    /**
     * On mouse up change the cursor back to the default
     * of the tool
     */
    onMouseUp = () => {
        this.setState('common', { cursor: this.cursor, tooltip: { title: '', visible: false, color: '#000000' } })
    }

    /**
     * On mouse drag if it is right click
     * or middle click then pan
     * @param event the paper mouse event
     * @returns
     */
    onMouseDrag = (event) => {
        this.toolPanning(event)
    }

    /**
     * Draw points on mouse down and once 3 points are
     * drawn create a path drawable and trigger the event
     * that will create a new drawable location from that
     * line, after that event is fired cleanup the points
     * previously drawn
     * @param event
     */
    public onMouseDown = (event: paper.ToolEvent): void => {
        // indicate pan is active on right click
        const clickCode = event['event']['button']

        if (this.isPanningClick(event)) return
        //this.path.add(newPoint)

        if (clickCode === MouseButtonCodes.Left) {
            this.temporaryPointObjects.push(this.renderPoint(event.point))

            const newPoint = event.point

            if (this.linePoints.length === 0) {
                this.mediator.mediate('2D', {
                    latestAnalyticsAction: {
                        startOrStop: AnalyticsActionEdgeType.START,
                        actionType: AnalyticsActionType.CREATE,
                    },
                })
            }

            this.linePoints.push([newPoint.x, newPoint.y])
            if (this.linePoints.length === RadiusLine.NUM_POINTS_IN_LINE) {
                this.temporaryPath?.remove()
                // Radius Line coordinates are stored in the following order: start, center, end
                const startPoint = this.linePoints[0]
                const centerPoint = this.linePoints[1]
                const endPoint = this.linePoints[2]

                // the created path always has exactly 3 points matching the coordinates above
                const newPath = this.createArc(startPoint, centerPoint, endPoint)

                newPath.strokeColor = this.lineAndCircleColor
                newPath.strokeWidth = this.strokeWidth
                if (newPath.data) newPath.data.shapeType = DRAWING_TYPES.RADIUS

                this.cleanUpPoints()
                this.setState('2D', { drawablesToCreate: [newPath.id] })
            } else {
                this.setScaleFromPointClick(newPoint)
            }
        }
    }

    /**
     * Creates a new paper Arc-Path from a collection of 2D Coords: start, center, end points.
     * The resulting path always has exactly 3 segments (one per coordinate)
     * @param startPoint
     * @param centerPoint
     * @param endPoint
     * @returns
     */
    public createArc = (startPoint: Vector2D, centerPoint: Vector2D, endPoint: Vector2D): paper.Path => {
        const start = new this.paper.Point(startPoint)
        const center = new this.paper.Point(centerPoint)
        const end = new this.paper.Point(endPoint)

        const segments = [start, center, end].map((point) => new this.paper.Segment(point))

        // Signed area of the triangle formed by the 3 points, its sign is the direction of
        // travel around the circle (start -> center -> end) and 0 means the points are collinear
        const ab = center.subtract(start)
        const bc = end.subtract(center)
        const orientation = ab.cross(bc)

        // Collinear / near-collinear points cannot form a stable arc; fall back to a straight 3 point line.
        // Use a scale-relative tolerance to avoid huge circumcenters when the signed area is tiny.
        const scale = ab.length * bc.length

        if (scale && Math.abs(orientation) > RadiusLine.COLLINEAR_TOLERANCE * scale) {
            const circleCenter = this.getCircleCenter(start, center, end, orientation)
            const direction = Math.sign(orientation)

            this.applyArcHandles(segments[0], segments[1], circleCenter, direction)
            this.applyArcHandles(segments[1], segments[2], circleCenter, direction)
        }

        return new this.paper.Path(segments)
    }

    /**
     * Calculates the center of the circle passing through the 3 provided points
     * @param a
     * @param b
     * @param c
     * @param orientation signed area of the triangle a, b, c (must not be 0)
     * @returns the circle center
     */
    private getCircleCenter = (a: paper.Point, b: paper.Point, c: paper.Point, orientation: number): paper.Point => {
        const d = 2 * orientation
        const aSquared = a.x * a.x + a.y * a.y
        const bSquared = b.x * b.x + b.y * b.y
        const cSquared = c.x * c.x + c.y * c.y

        const x = (aSquared * (b.y - c.y) + bSquared * (c.y - a.y) + cSquared * (a.y - b.y)) / d
        const y = (aSquared * (c.x - b.x) + bSquared * (a.x - c.x) + cSquared * (b.x - a.x)) / d

        return new this.paper.Point(x, y)
    }

    /**
     * Sets the bezier handles on two consecutive segments so the curve between them
     * follows the circle defined by circleCenter (same approximation paper uses in arcTo)
     * @param from segment the arc starts at
     * @param to segment the arc ends at
     * @param circleCenter center of the circle both segments lie on
     * @param direction direction of travel around the circle, 1 or -1
     */
    private applyArcHandles = (
        from: paper.Segment,
        to: paper.Segment,
        circleCenter: paper.Point,
        direction: number
    ): void => {
        const FULL_CIRCLE_DEG = 360
        const fromVector = from.point.subtract(circleCenter)
        const toVector = to.point.subtract(circleCenter)

        // getDirectedAngle returns the shortest angle between the vectors,
        // make sure the extent follows the direction of travel of the arc
        let extent = fromVector.getDirectedAngle(toVector)

        if (extent && Math.sign(extent) !== direction) extent += direction * FULL_CIRCLE_DEG
        if (!extent) return

        const half = (extent * Math.PI) / FULL_CIRCLE_DEG
        const handleLength = (4 / 3) * (Math.sin(half) / (1 + Math.cos(half)))

        const origin = new this.paper.Point(0, 0)

        from.handleOut = fromVector.rotate(90, origin).multiply(handleLength)
        to.handleIn = toVector.rotate(-90, origin).multiply(handleLength)
    }
}
